/**
 * useWeeklyAgendaCalendar Hook
 *
 * Manages state for the weekly agenda view.
 * Tracks current week start (always Monday), computes week data,
 * and exposes navigation actions.
 *
 * Follows single responsibility: weekly agenda state management only.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { WeeklyAgendaManager } from '@/lib/calendar/WeeklyAgendaManager';
import { LocalDateFormatter } from '@/lib/formatters/LocalDateFormatter';
import type { CalendarEvent } from '@/types/events';
import type { FamilyMember } from '@/types/family-member';
import type { CalendarMemberMapping } from '@/types/calendar-member-mapping';
import type { WeeklyAgendaWeekData } from '@/types/weekly-agenda';

type UseWeeklyAgendaCalendarParams = {
  allEvents: CalendarEvent[];
  familyMembers: FamilyMember[];
  calendarMappings: CalendarMemberMapping[];
  meMemberId: string;
  onWeekChange?: (mondayDateKey: string) => void;
};

type UseWeeklyAgendaCalendarResult = {
  currentWeekStart: Date;
  weekData: WeeklyAgendaWeekData;
  goToPrevWeek: () => void;
  goToNextWeek: () => void;
  goToToday: () => string;
  goToDate: (date: Date) => void;
  weekDateRange: { from: string; to: string };
};

export function useWeeklyAgendaCalendar({
  allEvents,
  familyMembers,
  calendarMappings,
  meMemberId,
  onWeekChange,
}: UseWeeklyAgendaCalendarParams): UseWeeklyAgendaCalendarResult {
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(
    () => WeeklyAgendaManager.getCurrentWeekMonday()
  );
  const onWeekChangeRef = useRef(onWeekChange);
  onWeekChangeRef.current = onWeekChange;

  const weekData = useMemo(() => {
    return WeeklyAgendaManager.buildWeekData(
      currentWeekStart,
      allEvents,
      familyMembers,
      calendarMappings,
      meMemberId
    );
  }, [currentWeekStart, allEvents, familyMembers, calendarMappings, meMemberId]);

  const weekDateRange = useMemo(() => {
    return WeeklyAgendaManager.getWeekDateRange(currentWeekStart);
  }, [currentWeekStart]);

  const goToPrevWeek = useCallback(() => {
    setCurrentWeekStart(prev => {
      const newStart = WeeklyAgendaManager.addDays(prev, -7);
      const mondayKey = LocalDateFormatter.toDateKey(newStart);
      onWeekChangeRef.current?.(mondayKey);
      return newStart;
    });
  }, []);

  const goToNextWeek = useCallback(() => {
    setCurrentWeekStart(prev => {
      const newStart = WeeklyAgendaManager.addDays(prev, 7);
      const mondayKey = LocalDateFormatter.toDateKey(newStart);
      onWeekChangeRef.current?.(mondayKey);
      return newStart;
    });
  }, []);

  const goToToday = useCallback((): string => {
    const todayMonday = WeeklyAgendaManager.getCurrentWeekMonday();
    setCurrentWeekStart(todayMonday);
    const todayKey = LocalDateFormatter.getTodayDateString();
    onWeekChangeRef.current?.(LocalDateFormatter.toDateKey(todayMonday));
    return todayKey;
  }, []);

  const goToDate = useCallback((date: Date) => {
    const monday = WeeklyAgendaManager.getMondayOfWeek(date);
    setCurrentWeekStart(monday);
    const mondayKey = LocalDateFormatter.toDateKey(monday);
    onWeekChangeRef.current?.(mondayKey);
  }, []);

  return {
    currentWeekStart,
    weekData,
    goToPrevWeek,
    goToNextWeek,
    goToToday,
    goToDate,
    weekDateRange,
  };
}
